import {
  getReloadActivitySafety,
  type ReloadSafety,
} from '@/lib/reload-blockers';
import { isComposerDraft } from './composer-draft-validation';
import { emptyComposerDraft } from './empty-composer-draft';
import type { ComposerViewProps } from './composer-view-types';

function comparableDraft(value: unknown) {
  return JSON.stringify(value, (_key, entry: unknown) => {
    if (typeof entry === 'string' && entry.startsWith('blob:'))
      return undefined;
    if (typeof entry === 'object' && entry !== null && !Array.isArray(entry)) {
      return Object.fromEntries(
        Object.entries(entry).sort(([a], [b]) => a.localeCompare(b))
      );
    }
    return entry;
  });
}

export function getComposerDraftReloadSafety(draft: unknown): ReloadSafety {
  if (!isComposerDraft(draft)) return 'unknown';
  return comparableDraft(draft) === comparableDraft(emptyComposerDraft(draft))
    ? 'safe'
    : 'blocked';
}

type ComposerReloadState = Pick<
  ComposerViewProps,
  | 'hasUploading'
  | 'isProcessingFiles'
  | 'isSending'
  | 'isQueueing'
  | 'isPreparingSheetAnnotations'
  | 'awaitingUploadsSend'
  | 'pendingDroppedFiles'
  | 'queueActionId'
  | 'attachments'
  | 'productUploads'
  | 'patternUploads'
  | 'designUploads'
  | 'colorUploads'
  | 'orderedQueuedMessages'
  | 'editingQueueItemId'
  | 'queueEditOpen'
> & {
  deliveryRecovery: Pick<
    ComposerViewProps['deliveryRecovery'],
    'failures' | 'retrying'
  >;
};

export function getComposerViewReloadSafety(
  props: ComposerReloadState
): ReloadSafety {
  const transient =
    props.hasUploading ||
    props.isProcessingFiles ||
    props.isSending ||
    props.isQueueing ||
    props.awaitingUploadsSend ||
    props.isPreparingSheetAnnotations ||
    (props.pendingDroppedFiles?.length ?? 0) > 0 ||
    props.queueActionId !== null;
  const unsent =
    props.attachments.length +
      props.productUploads.length +
      props.patternUploads.length +
      props.designUploads.length +
      props.colorUploads.length +
      props.orderedQueuedMessages.length >
      0 ||
    props.editingQueueItemId !== null ||
    props.queueEditOpen ||
    props.deliveryRecovery.failures.length > 0 ||
    props.deliveryRecovery.retrying !== null;
  // Complete draft content is tracked by the session writer, including editor text
  // and non-text references. These flags cover in-flight work independently.
  return getReloadActivitySafety({
    draftDirty: unsent,
    uploads: transient ? 1 : 0,
  });
}
